/**
 * Admin-API – organisationsübergreifende Verwaltungsoperationen.
 * Erfordert die Permission `admin:write`.
 */
import { api } from "./client";

export interface AdminOrg {
  id: string | null;
  external_id: string;
  display_name: string;
  name: string;
}

export interface AdminOrgMember {
  user_id: string;
  email: string;
  name: string;
  picture?: string;
  first_name?: string | null;
  last_name?: string | null;
  last_login?: string | null;
}

export interface AdminMemberDetail {
  user_id: string;
  email: string;
  name: string;
  picture?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  last_login?: string | null;
  first_login?: string | null;
  roles: AdminRole[];
}

export interface AdminRole {
  id: string;
  name: string;
  description?: string;
}

export interface AdminUserInvite {
  email: string;
  first_name: string;
  last_name: string;
  role_ids: string[];
}

export interface AdminGesellschaft {
  id: string;
  organisation_id: string;
  name: string;
  rechtsform: string | null;
  typ: "haupt" | "verwaltung";
}

export interface AdminGesellschaftCreate {
  name: string;
  rechtsform?: string | null;
  typ: "haupt" | "verwaltung";
}

export interface AdminGesellschaftUpdate {
  name?: string;
  rechtsform?: string | null;
}

export interface AdminAbzug {
  id: string;
  organisation_id: string;
  bezeichnung: string;
  wert: number;
  sort_order: number;
}

export interface AdminAbzugCreate {
  bezeichnung: string;
  wert?: number;
}

export interface AdminAbzugUpdate {
  bezeichnung?: string;
  wert?: number;
}

export interface AdminScoreSettings {
  irr_min: number;
  irr_ziel: number;
  investitionsvolumen_ziel: number;
  investitionsvolumen_sigma: number;
  ek_quote_min: number;
  ek_quote_max: number;
  gewicht_irr: number;
  gewicht_investitionsvolumen: number;
  gewicht_ek_quote: number;
}

export type AdminScoreSettingsUpdate = AdminScoreSettings;

export interface AdminReifegradSettings {
  gewicht_flaeche: number;
  gewicht_planungsrecht: number;
  gewicht_genehmigung: number;
  gewicht_ausschreibung: number;
  gewicht_finanzierung: number;
  gewicht_bau: number;
}

export type AdminReifegradSettingsUpdate = AdminReifegradSettings;

export interface AdminTemplateItem {
  id: string;
  section_id: string;
  bezeichnung: string;
  is_locked: boolean;
  sort_order: number;
}

export interface AdminTemplateSection {
  id: string;
  organisation_id: string;
  section_key: string;
  custom_label: string;
  is_locked: boolean;
  sort_order: number;
  items: AdminTemplateItem[];
}

export interface AdminTemplateSectionCreate {
  custom_label: string;
}

export interface AdminTemplateSectionUpdate {
  custom_label: string;
}

export interface AdminTemplateItemCreate {
  bezeichnung: string;
}

export interface AdminTemplateItemUpdate {
  bezeichnung: string;
}

export const adminApi = {
  async getOrganisations(): Promise<AdminOrg[]> {
    return api.get("/admin/organisations");
  },

  async createOrganisation(data: { display_name: string }): Promise<AdminOrg> {
    return api.post("/admin/organisations", data);
  },

  async getOrgMembers(orgId: string): Promise<AdminOrgMember[]> {
    return api.get(`/admin/organisations/${orgId}/members`);
  },

  async getMemberDetail(
    orgId: string,
    userId: string,
  ): Promise<AdminMemberDetail> {
    return api.get(
      `/admin/organisations/${orgId}/members/${encodeURIComponent(userId)}`,
    );
  },

  async getRoles(): Promise<AdminRole[]> {
    return api.get("/admin/roles");
  },

  async inviteUser(orgId: string, data: AdminUserInvite): Promise<void> {
    return api.post(`/admin/organisations/${orgId}/invite`, data);
  },

  async getGesellschaften(orgId: string): Promise<AdminGesellschaft[]> {
    return api.get(`/admin/organisations/${orgId}/gesellschaften`);
  },

  async createGesellschaft(
    orgId: string,
    data: AdminGesellschaftCreate,
  ): Promise<AdminGesellschaft> {
    return api.post(`/admin/organisations/${orgId}/gesellschaften`, data);
  },

  async updateGesellschaft(
    orgId: string,
    gesellschaftId: string,
    data: AdminGesellschaftUpdate,
  ): Promise<AdminGesellschaft> {
    return api.put(
      `/admin/organisations/${orgId}/gesellschaften/${gesellschaftId}`,
      data,
    );
  },

  async deleteGesellschaft(
    orgId: string,
    gesellschaftId: string,
  ): Promise<void> {
    return api.delete(
      `/admin/organisations/${orgId}/gesellschaften/${gesellschaftId}`,
    );
  },

  async getRechtsformOptions(): Promise<string[]> {
    return api.get("/admin/gesellschaft/rechtsform-options");
  },

  async getAbzuege(orgId: string): Promise<AdminAbzug[]> {
    return api.get(`/admin/organisations/${orgId}/abzuege`);
  },

  async createAbzug(
    orgId: string,
    data: AdminAbzugCreate,
  ): Promise<AdminAbzug> {
    return api.post(`/admin/organisations/${orgId}/abzuege`, data);
  },

  async updateAbzug(
    orgId: string,
    abzugId: string,
    data: AdminAbzugUpdate,
  ): Promise<AdminAbzug> {
    return api.put(`/admin/organisations/${orgId}/abzuege/${abzugId}`, data);
  },

  async deleteAbzug(orgId: string, abzugId: string): Promise<void> {
    return api.delete(`/admin/organisations/${orgId}/abzuege/${abzugId}`);
  },

  async getScoreSettings(orgId: string): Promise<AdminScoreSettings> {
    return api.get(`/admin/organisations/${orgId}/score-settings`);
  },

  async updateScoreSettings(
    orgId: string,
    data: AdminScoreSettingsUpdate,
  ): Promise<AdminScoreSettings> {
    return api.put(`/admin/organisations/${orgId}/score-settings`, data);
  },

  async getReifegradSettings(orgId: string): Promise<AdminReifegradSettings> {
    return api.get(`/admin/organisations/${orgId}/reifegrad-settings`);
  },

  async updateReifegradSettings(
    orgId: string,
    data: AdminReifegradSettingsUpdate,
  ): Promise<AdminReifegradSettings> {
    return api.put(`/admin/organisations/${orgId}/reifegrad-settings`, data);
  },

  // CAPEX-Template
  async getCapexTemplate(orgId: string): Promise<AdminTemplateSection[]> {
    return api.get(`/admin/organisations/${orgId}/capex-template`);
  },

  async addCapexSection(
    orgId: string,
    data: AdminTemplateSectionCreate,
  ): Promise<AdminTemplateSection> {
    return api.post(
      `/admin/organisations/${orgId}/capex-template/sections`,
      data,
    );
  },

  async updateCapexSection(
    orgId: string,
    sectionId: string,
    data: AdminTemplateSectionUpdate,
  ): Promise<AdminTemplateSection> {
    return api.put(
      `/admin/organisations/${orgId}/capex-template/sections/${sectionId}`,
      data,
    );
  },

  async deleteCapexSection(orgId: string, sectionId: string): Promise<void> {
    return api.delete(
      `/admin/organisations/${orgId}/capex-template/sections/${sectionId}`,
    );
  },

  async addCapexItem(
    orgId: string,
    sectionId: string,
    data: AdminTemplateItemCreate,
  ): Promise<AdminTemplateItem> {
    return api.post(
      `/admin/organisations/${orgId}/capex-template/sections/${sectionId}/items`,
      data,
    );
  },

  async updateCapexItem(
    orgId: string,
    sectionId: string,
    itemId: string,
    data: AdminTemplateItemUpdate,
  ): Promise<AdminTemplateItem> {
    return api.put(
      `/admin/organisations/${orgId}/capex-template/sections/${sectionId}/items/${itemId}`,
      data,
    );
  },

  async deleteCapexItem(
    orgId: string,
    sectionId: string,
    itemId: string,
  ): Promise<void> {
    return api.delete(
      `/admin/organisations/${orgId}/capex-template/sections/${sectionId}/items/${itemId}`,
    );
  },

  // OPEX-Template
  async getOpexTemplate(orgId: string): Promise<AdminTemplateSection[]> {
    return api.get(`/admin/organisations/${orgId}/opex-template`);
  },

  async addOpexSection(
    orgId: string,
    data: AdminTemplateSectionCreate,
  ): Promise<AdminTemplateSection> {
    return api.post(
      `/admin/organisations/${orgId}/opex-template/sections`,
      data,
    );
  },

  async updateOpexSection(
    orgId: string,
    sectionId: string,
    data: AdminTemplateSectionUpdate,
  ): Promise<AdminTemplateSection> {
    return api.put(
      `/admin/organisations/${orgId}/opex-template/sections/${sectionId}`,
      data,
    );
  },

  async deleteOpexSection(orgId: string, sectionId: string): Promise<void> {
    return api.delete(
      `/admin/organisations/${orgId}/opex-template/sections/${sectionId}`,
    );
  },

  async addOpexItem(
    orgId: string,
    sectionId: string,
    data: AdminTemplateItemCreate,
  ): Promise<AdminTemplateItem> {
    return api.post(
      `/admin/organisations/${orgId}/opex-template/sections/${sectionId}/items`,
      data,
    );
  },

  async updateOpexItem(
    orgId: string,
    sectionId: string,
    itemId: string,
    data: AdminTemplateItemUpdate,
  ): Promise<AdminTemplateItem> {
    return api.put(
      `/admin/organisations/${orgId}/opex-template/sections/${sectionId}/items/${itemId}`,
      data,
    );
  },

  async deleteOpexItem(
    orgId: string,
    sectionId: string,
    itemId: string,
  ): Promise<void> {
    return api.delete(
      `/admin/organisations/${orgId}/opex-template/sections/${sectionId}/items/${itemId}`,
    );
  },
};
